import assetDepartmentService from "../../../../services/assetService/assetDepartmentService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETDEPARTMENTS } from "../../../../utils/constants";
import { AssetDepartmentInterface } from "../../../../models/AssetModel/AssetDepartment";

interface DeleteAssetDepartmentContext {
  previousAssetDepartments: AssetDepartmentInterface[];
  deletedAssetDepartment: AssetDepartmentInterface | undefined;
}

const useDeleteAssetDepartment = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the category to delete
    DeleteAssetDepartmentContext
  >({
    mutationFn: assetDepartmentService.Remove, // Remove should take a string ID

    onMutate: (assetDepartmentId: string) => {
      const previousAssetDepartments =
        queryClient.getQueryData<AssetDepartmentInterface[]>([
          CACHE_KEY_ASSETDEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<AssetDepartmentInterface[]>(
        [
          CACHE_KEY_ASSETDEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousAssetDepartments.filter(
          (assetDepartment) => assetDepartment.id !== assetDepartmentId
        ) // Use assetDepartmentId to filter
      );

      return {
        previousAssetDepartments,
        deletedAssetDepartment: previousAssetDepartments.find(
          (assetDepartment) => assetDepartment.id === assetDepartmentId
        ),
      };
    },

    onError: (error, assetDepartmentId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<AssetDepartmentInterface[]>(
        [
          CACHE_KEY_ASSETDEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousAssetDepartments
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteAssetDepartment;
